import { useLocation } from "react-router-dom";
import { useBuyPlans } from "../useHook";
import { useEffect, useState } from "react";
import HouseDesignDropdownModal from "../../../Components/Modals/AssociateHousePlans/HouseDesignDropdownModal";
import Swal from "sweetalert2";
import { MdDelete } from "react-icons/md";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

function BuyThisPlanDetails() {
  const { hasPermission } = usePermission();
  const canAssociate = hasPermission(SystemPermissions.StandardPlanPricing.ASSOCIATE_HOUSE_PLAN);
  const canUpdateAssociate = hasPermission(SystemPermissions.StandardPlanPricing.UPDATE_ASSOCIATE_HOUSE_PLAN);
  const canDeleteAssociate = hasPermission(SystemPermissions.StandardPlanPricing.DELETE_AASSOCIATE_HOUSE_PLAN);

  const {
    associateBuyPlanToHouseDesing,
    getExcludedHousePlans,
    getBuyPlanById,
    getBuyPlanByHouseDesignId,
    updateBuyAssociatedPlan,
    deleteBuyPlanFromHouseDesign,
  } = useBuyPlans();

  const location = useLocation();
  const { buyPlanId } = location.state || {};

  const [excludedHouseDesigns, setExcludedHouseDesigns] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buyPlanByIdData, setBuyPlanByIdData] = useState(null);
  const [buyPlanByHouseDesignIdData, setBuyPlanByHouseDesignIdData] = useState(
    []
  );
  const [selectedKeys, setSelectedKeys] = useState({});

  const fetchAllData = async () => {
    if (!buyPlanId) return;
    getExcludedHousePlans(buyPlanId, setExcludedHouseDesigns);
    getBuyPlanById(buyPlanId, setBuyPlanByIdData);
    getBuyPlanByHouseDesignId(buyPlanId, (data) => {
      setBuyPlanByHouseDesignIdData(data);
      const initialSelections = {};
      data.forEach((item) => {
        const priceOptions = Object.entries(item.buyPlan || {}).filter(
          ([key]) => key !== "id" && key !== "name"
        );
        const matchedKey = priceOptions.find(
          ([, value]) => value === item.price
        )?.[0];
        if (matchedKey) initialSelections[item.id] = matchedKey;
      });
      setSelectedKeys(initialSelections);
    });
  };

  useEffect(() => {
    fetchAllData();
  }, [buyPlanId]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleAssociate = async (house_design_id, price) => {
    if (!house_design_id || !price) return;
    const response = await associateBuyPlanToHouseDesing({
      house_design_id,
      buy_plan_id: buyPlanId,
      price,
    });
    if (response?.status === 201 || response?.status === 200 || response?.data?.success !== false) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "House design associated successfully!",
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to associate house design. Please try again.",
      });
    }
    closeModal();
    await fetchAllData();
  };

  const handleUpdate = async (item) => {
    const selectedKey = selectedKeys[item.id];
    const updatedPrice = item.buyPlan?.[selectedKey];
    if (!selectedKey || !updatedPrice) return;
    const body = {
      house_design_id: item.houseDesign.id,
      buy_plan_id: item.buyPlan.id,
      price: updatedPrice,
    };
    const response = await updateBuyAssociatedPlan(body);
    if (
      response?.status === 200 ||
      response?.status === true ||
      response?.success === true
    ) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "House design plan updated successfully!",
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update house design plan. Please try again.",
      });
    }
    await fetchAllData();
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const response = await deleteBuyPlanFromHouseDesign(id);
        if (response) {
          await fetchAllData();
          Swal.fire({
            title: "Deleted!",
            text: "The product has been deleted successfully.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        }
        await fetchAllData();
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Failed to delete the product. Please try again.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  return (
    <>
      <div className="bg-gray-100 p-4 flex justify-between">
        <h2 className="text-2xl">
          Manage House Design Plans for{" "}
          <span className="font-bold">{buyPlanByIdData?.name || ""}</span>
        </h2>
        {canAssociate && (
          <button
            className="btn btn-primary h-[44px]"
            onClick={openModal}
          >
            <FiPlus className="text-white" />
            Associate House Plan
          </button>
        )}
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
        {buyPlanByHouseDesignIdData.length === 0 ? (
          <div className="col-span-full text-center text-gray-500 text-lg">
            No data found
          </div>
        ) : (
          buyPlanByHouseDesignIdData.map((item) => {
            const house = item.houseDesign || {};
            const buyPlan = item.buyPlan || {};
            const priceOptions = Object.entries(buyPlan).filter(
              ([key]) => key !== "id" && key !== "name"
            );
            const matchedKey = priceOptions.find(
              ([, value]) => value === item.price
            )?.[0];
            const selectedKey = selectedKeys[item.id] || "";

            return (
              <div
                key={item.id}
                className="bg-white rounded shadow-md p-4 border border-gray-200"
              >
                <div className="flex justify-between items-center gap-3 mb-2">
                  <h3
                    className="text-lg font-semibold min-w-0 flex-1 truncate"
                    title={house.title || "Untitled House"}
                  >
                    {house.title || "Untitled House"}
                  </h3>
                  {canDeleteAssociate && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="btn btn-delete text-white shrink-0 justify-center"
                      title="Delete"
                    >
                      <MdDelete className="text-white" />
                      <span>Delete</span>
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Plot: {house.plot_dimension || "N/A"} | Area:{" "}
                  {house.covered_area || "N/A"}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  Floors: {house.floors || 0} | Beds: {house.bed_rooms || 0} |
                  Baths: {house.bath_rooms || 0}
                </p>

                <select
                  value={selectedKey}
                  onChange={(e) =>
                    setSelectedKeys((prev) => ({
                      ...prev,
                      [item.id]: e.target.value,
                    }))
                  }
                  disabled={!canUpdateAssociate}
                  className={`w-full p-2 border rounded mt-2 bg-white ${
                    !canUpdateAssociate ? "cursor-not-allowed opacity-75" : ""
                  }`}
                >
                  <option value="">Select Price</option>
                  {priceOptions.map(([key, value]) => (
                    <option key={key} value={key}>
                      {`${key.replaceAll("_", " ")} - Rs. ${value}`}
                    </option>
                  ))}
                </select>

                {selectedKey && selectedKey !== matchedKey && canUpdateAssociate && (
                  <button
                    onClick={() => handleUpdate(item)}
                    className="mt-3 btn btn-edit text-white"
                  >
                    <FiEdit2 className="text-white" /> Update
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <HouseDesignDropdownModal
        isOpen={isModalOpen}
        onClose={closeModal}
        houseDesigns={excludedHouseDesigns}
        buyThisPlan={buyPlanByIdData}
        onSubmit={handleAssociate}
      />
    </>
  );
}

export default BuyThisPlanDetails;
